/**
 * O primeiro toque de quem chega no app: a página que mandou a pessoa e a primeira página que ela
 * viu na visita.
 *
 * Existe para o balde que `utils/captureAttribution` não enxerga. Os parâmetros de campanha só
 * aparecem quando o link os carrega — e orgânico, direto, link em bio, Telegram e WhatsApp nunca
 * carregam. Medido em 01/10/2026: entre os vendedores cadastrados desde 06/09, 52% chegaram sem
 * nenhum parâmetro, contra 18-27% entre todos os cadastros. O canal que mais produz vendedor é
 * justamente o que o banco não via.
 *
 * Fica separado da origem de campanha DE PROPÓSITO. Lá a regra é primeiro toque PAGO: uma visita
 * direta não pode bloquear o anúncio que vier depois. Se a página de entrada morasse no mesmo objeto,
 * toda primeira visita gravaria algo e travaria a campanha seguinte. Aqui são dois fatos
 * independentes — de onde a pessoa chegou da primeira vez, e qual anúncio a trouxe — e cada um tem a
 * própria memória.
 *
 * O ciclo de vida é o mesmo da origem de campanha (capturado só sem sessão, apagado no cadastro e no
 * login): `captureAttribution` chama este módulo nos dois pontos.
 */
import { ENTRY_SEARCH } from '@utils/entrySearch';

/** Teto das colunas `first_touch_referrer` e `first_touch_landing_page` (changeset 654). */
export const FIRST_TOUCH_URL_MAX = 255;

/** Mesma janela da origem de campanha — ver `ATTRIBUTION_TTL_DAYS`. */
export const FIRST_TOUCH_TTL_DAYS = 30;

export const FIRST_TOUCH_LOCAL_STORAGE_KEY = 'firstTouch';

/**
 * Parâmetros com que o site (`vibx.com.br`) repassa o PRÓPRIO primeiro toque no clique do CTA de
 * cadastro (`campaignForwarding.ts` no vibx-site). Sem eles, o `document.referrer` aqui seria sempre
 * o site, e a origem real — o Google, o Instagram — se perderia no salto de domínio.
 */
export const FORWARDED_REFERRER_PARAM = 'vx_ref';
export const FORWARDED_LANDING_PARAM = 'vx_lp';

/**
 * As chaves são as do corpo do cadastro (e as do `platform_user`): prefixo `firstTouch` porque
 * `referrerCode` já existe e é outra coisa — o código do afiliado que indicou a pessoa.
 */
export type FirstTouch = {
  firstTouchReferrer?: string;
  firstTouchLandingPage?: string;
};

type FirstTouchGuardado = { firstTouch: FirstTouch; capturedAt: string };

const TTL_MS = FIRST_TOUCH_TTL_DAYS * 24 * 60 * 60 * 1000;

/*
  Instantâneos da avaliação do módulo, pelo mesmo motivo do `ENTRY_SEARCH`: a captura corre num
  efeito do `_app`, depois da pintura, e uma navegação de cliente nessa janela já trocou a URL. O
  `document.referrer` não muda com `pushState`, mas é lido aqui junto para os dois serem da mesma
  entrada.
*/
const ENTRY_REFERRER = typeof document === 'undefined' ? '' : document.referrer;
const ENTRY_HREF = typeof window === 'undefined' ? '' : window.location.href;

/**
 * Origem + caminho, ou `undefined`.
 *
 * Sem query string e sem fragmento: é ali que link de e-mail, link mágico e buscador carregam dado
 * pessoal e token. Só `http`/`https`. A API repete a mesma regra, porque o corpo pode chegar de
 * qualquer lugar; repetir aqui é para o dado nem sair do navegador.
 */
export const reduceToOriginAndPath = (valor: unknown): string | undefined => {
  if (typeof valor !== 'string' || !valor.trim()) return undefined;

  try {
    const url = new URL(valor.trim());
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;

    return `${url.origin}${url.pathname}`.slice(0, FIRST_TOUCH_URL_MAX);
  } catch {
    return undefined;
  }
};

/**
 * O primeiro toque desta entrada.
 *
 * **O que o site repassou ganha.** Chegando pelo CTA do site, `vx_lp` está sempre presente e o par
 * repassado descreve a visita inteira — inclusive quando `vx_ref` falta, que é a entrada direta no
 * site. Cair no `document.referrer` nesse caso registraria o próprio site como origem.
 *
 * Sem repasse, vale o que este documento viu: o referrer, se veio de outro host (navegação interna
 * não é origem), e a própria URL de entrada.
 */
export const parseFirstTouch = ({
  search = ENTRY_SEARCH,
  referrer = ENTRY_REFERRER,
  href = ENTRY_HREF,
}: { search?: string; referrer?: string; href?: string } = {}): FirstTouch => {
  const params = new URLSearchParams(search);
  const landingRepassada = reduceToOriginAndPath(
    params.get(FORWARDED_LANDING_PARAM),
  );

  if (landingRepassada) {
    const referrerRepassado = reduceToOriginAndPath(
      params.get(FORWARDED_REFERRER_PARAM),
    );

    return {
      firstTouchLandingPage: landingRepassada,
      ...(referrerRepassado && { firstTouchReferrer: referrerRepassado }),
    };
  }

  const landingPage = reduceToOriginAndPath(href);
  let origemPropria: string | undefined;
  try {
    origemPropria = href ? new URL(href).origin : undefined;
  } catch {
    origemPropria = undefined;
  }

  const referrerReduzido = reduceToOriginAndPath(referrer);
  const externo =
    referrerReduzido && new URL(referrerReduzido).origin !== origemPropria
      ? referrerReduzido
      : undefined;

  return {
    ...(landingPage && { firstTouchLandingPage: landingPage }),
    ...(externo && { firstTouchReferrer: externo }),
  };
};

const expirou = (capturedAt: unknown) => {
  if (typeof capturedAt !== 'string') return true;

  const quando = new Date(capturedAt).getTime();

  return Number.isNaN(quando) || Date.now() - quando > TTL_MS;
};

export const removeFirstTouchFromLocalStorage = () => {
  try {
    localStorage.removeItem(FIRST_TOUCH_LOCAL_STORAGE_KEY);
  } catch {
    /* Limpeza de metadado não interrompe nada. */
  }
};

/**
 * O primeiro toque guardado, ou objeto vazio. Nunca lança — é lido no meio do envio do cadastro,
 * mesma razão do `getAttributionFromLocalStorage`.
 */
export const getFirstTouchFromLocalStorage = (): FirstTouch => {
  try {
    const bruto = localStorage.getItem(FIRST_TOUCH_LOCAL_STORAGE_KEY);
    if (!bruto) return {};

    const guardado = JSON.parse(bruto);
    if (!guardado || typeof guardado !== 'object' || Array.isArray(guardado))
      return {};

    if (expirou(guardado.capturedAt)) {
      removeFirstTouchFromLocalStorage();
      return {};
    }

    const toque = guardado.firstTouch ?? {};
    const referrer = reduceToOriginAndPath(toque.firstTouchReferrer);
    const landingPage = reduceToOriginAndPath(toque.firstTouchLandingPage);

    return {
      ...(referrer && { firstTouchReferrer: referrer }),
      ...(landingPage && { firstTouchLandingPage: landingPage }),
    };
  } catch {
    return {};
  }
};

/**
 * Guarda o primeiro toque desta entrada, **sem sobrescrever um ainda válido**. Quem chega pelo
 * Google, sai, e volta digitando o endereço antes de se cadastrar continua com o Google como origem.
 */
export const captureFirstTouch = (entrada?: {
  search?: string;
  referrer?: string;
  href?: string;
}) => {
  const toque = parseFirstTouch(entrada);
  if (!Object.keys(toque).length) return;
  if (Object.keys(getFirstTouchFromLocalStorage()).length) return;

  const guardado: FirstTouchGuardado = {
    firstTouch: toque,
    capturedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(
      FIRST_TOUCH_LOCAL_STORAGE_KEY,
      JSON.stringify(guardado),
    );
  } catch {
    /* Armazenamento indisponível não interrompe a visita. */
  }
};
